import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Coins, Sparkles, Filter, Loader2, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TopUpDialog from '../../components/credits/TopUpDialog';

const FEATURES = ['all', 'topup', 'migration', 'agent_chat', 'budgets'];

export default function CreditsUsagePage() {
  const account = getCurrentAccount();
  const [wallet, setWallet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [txs, setTxs] = useState([]);
  const [feature, setFeature] = useState('all');
  const [busy, setBusy] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);

  const load = useCallback(async () => {
    if (!account?.id) return;
    setBusy(true);
    try {
      const p = { params: { account_id: account.id } };
      const walletP = api.get('/credits/wallet', p);
      const ledgerP = api.get('/credits/ledger', {
        params: { account_id: account.id, limit: 200, ...(feature !== 'all' ? { feature } : {}) },
      });
      const txP = api.get('/credits/transactions', p);
      const [w, l, t] = await Promise.all([walletP, ledgerP, txP]);
      setWallet(w.data);
      setEntries(l.data.entries || []);
      setTxs(t.data.transactions || []);
    } finally { setBusy(false); }
  }, [account?.id, feature]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const debits = entries.filter((e) => e.kind === 'debit')
      .reduce((s, e) => s + (e.credits || 0), 0);
    const credits = entries.filter((e) => e.kind === 'credit')
      .reduce((s, e) => s + (e.credits || 0), 0);
    return { debits: debits.toFixed(1), credits: credits.toFixed(1) };
  }, [entries]);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-4" data-testid="credits-usage-page">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" /> Credits &amp; Usage
          </h1>
          <Button
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 ml-auto"
            onClick={() => setTopupOpen(true)}
            data-testid="usage-topup-btn"
          >
            <Sparkles className="h-4 w-4 mr-1" /> Top up
          </Button>
        </div>

        {/* Wallet summary tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="usage-summary-tiles">
          <Tile label="Balance"        value={`${(wallet?.balance_credits ?? 0).toFixed(1)}`} sub={`≈ $${(wallet?.balance_usd ?? 0).toFixed(2)}`} tone={wallet?.empty ? 'rose' : wallet?.low_balance ? 'amber' : 'emerald'} />
          <Tile label="Purchased"      value={`${(wallet?.purchased_credits ?? 0).toFixed(1)}`} sub="total top-ups" tone="slate" />
          <Tile label="Consumed"       value={`${(wallet?.consumed_credits ?? 0).toFixed(1)}`} sub="total spend"    tone="slate" />
          <Tile label="Granted"        value={`${(wallet?.granted_credits ?? 0).toFixed(1)}`} sub="promos / gifts" tone="slate" />
        </div>

        {/* Feature filter */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="usage-filter">
          <Filter className="h-4 w-4 text-slate-400" />
          {FEATURES.map((f) => (
            <button
              key={f}
              data-testid={`usage-filter-${f}`}
              onClick={() => setFeature(f)}
              className={`text-xs px-2.5 py-1 rounded-none border ${
                feature === f
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
              }`}
            >
              {f}
            </button>
          ))}
          <span className="text-xs text-slate-500 ml-auto">
            Filtered: <span className="text-emerald-700 font-semibold">+{totals.credits}</span>{' '}
            / <span className="text-rose-700 font-semibold">-{totals.debits}</span> credits
          </span>
        </div>

        {/* Ledger table */}
        <div className="border rounded-none overflow-x-auto bg-white" data-testid="usage-ledger-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="p-2">When</th>
                <th className="p-2">Feature</th>
                <th className="p-2">Action</th>
                <th className="p-2">Model</th>
                <th className="p-2 text-right">Tokens (in / out)</th>
                <th className="p-2 text-right">Credits</th>
              </tr>
            </thead>
            <tbody>
              {busy && entries.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-slate-500"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading…</td></tr>
              )}
              {!busy && entries.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-slate-400">No usage yet</td></tr>
              )}
              {entries.map((e) => {
                const isCredit = e.kind === 'credit';
                return (
                  <tr key={e.id} className="border-t" data-testid={`ledger-row-${e.id}`}>
                    <td className="p-2 whitespace-nowrap text-slate-500">
                      {new Date(e.ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="p-2 capitalize">
                      <Badge variant="outline" className="rounded-none text-[10px]">{e.feature || '—'}</Badge>
                    </td>
                    <td className="p-2 text-slate-700">{e.action || '—'}</td>
                    <td className="p-2 text-slate-500">{e.usage?.model || '—'}</td>
                    <td className="p-2 text-right text-slate-600">
                      {e.usage?.input_tokens != null
                        ? `${e.usage.input_tokens} / ${e.usage.output_tokens || 0}`
                        : '—'}
                    </td>
                    <td className={`p-2 text-right font-mono font-semibold whitespace-nowrap ${isCredit ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {isCredit
                        ? <><ArrowUpCircle className="h-3 w-3 inline mr-1" />+{(e.credits || 0).toFixed(2)}</>
                        : <><ArrowDownCircle className="h-3 w-3 inline mr-1 text-rose-500" />-{(e.credits || 0).toFixed(2)}</>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Recent top-up receipts */}
        {txs.length > 0 && (
          <div className="mt-4" data-testid="usage-tx-block">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Recent top-ups</h2>
            <div className="border rounded-none bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="p-2">Date</th>
                    <th className="p-2">Package</th>
                    <th className="p-2 text-right">Amount</th>
                    <th className="p-2 text-right">Credits</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id} className="border-t" data-testid={`tx-row-${t.id}`}>
                      <td className="p-2 whitespace-nowrap text-slate-500">
                        {new Date(t.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="p-2 capitalize">{t.package_id}</td>
                      <td className="p-2 text-right font-mono">${t.amount_usd.toFixed(2)}</td>
                      <td className="p-2 text-right font-mono">{t.credits}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={`rounded-none text-[10px] ${
                          t.payment_status === 'paid' ? 'border-emerald-400 text-emerald-700' :
                          t.payment_status === 'failed' ? 'border-rose-400 text-rose-700' :
                          'border-slate-400 text-slate-600'
                        }`}>{t.payment_status}</Badge>
                      </td>
                      <td className="p-2 text-slate-400 font-mono text-[10px]">{t.session_id?.slice(-14)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <TopUpDialog open={topupOpen} onOpenChange={setTopupOpen} wallet={wallet} onOpened={load} />
    </MainLayout>
  );
}

function Tile({ label, value, sub, tone = 'slate' }) {
  const map = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-300 bg-amber-50 text-amber-800',
    rose: 'border-rose-300 bg-rose-50 text-rose-800',
    slate: 'border-slate-200 bg-white text-slate-800',
  };
  return (
    <div className={`rounded-none border p-3 ${map[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}
